import React, { useMemo, useRef, useState } from "react";
import { Button, Empty, Input, Popover, Tooltip, Upload } from "antd";
import {
  SendOutlined, SmileOutlined, PaperClipOutlined, ThunderboltOutlined,
} from "@ant-design/icons";
import EmojiPicker from "emoji-picker-react";
import {
  MdImage, MdVideocam, MdAudiotrack, MdInsertDriveFile,
  MdLocationOn, MdContacts,
} from "react-icons/md";

const { TextArea } = Input;

/**
 * The composer, shared by both channels.
 *
 * WhatsApp sends one message per file and offers location and contact cards;
 * email stages several attachments and sends them with the body as one mail.
 * That difference is why `attachmentContent` and `enterSends` are props rather
 * than assumptions.
 */

export const WhatsAppAttachmentMenu = ({ onPickFile, onLocation, onContact }) => {
  const tiles = [
    { key: "image", label: "Photo", icon: <MdImage />, color: "#7f66ff", accept: "image/*" },
    { key: "video", label: "Video", icon: <MdVideocam />, color: "#ff2e74", accept: "video/*" },
    { key: "audio", label: "Audio", icon: <MdAudiotrack />, color: "#ff8f00", accept: "audio/*" },
    { key: "document", label: "Document", icon: <MdInsertDriveFile />, color: "#5157ae", accept: "*" },
  ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 72px)", gap: 8 }}>
      {tiles.map((tile) => (
        <Upload
          key={tile.key}
          accept={tile.accept}
          showUploadList={false}
          beforeUpload={(file) => {
            onPickFile(file, tile.key);
            // Returning false keeps antd from trying to upload it itself.
            return false;
          }}
        >
          <Tile icon={tile.icon} label={tile.label} color={tile.color} />
        </Upload>
      ))}
      <Tile icon={<MdLocationOn />} label="Location" color="#1fa855" onClick={onLocation} />
      <Tile icon={<MdContacts />} label="Contact" color="#0288d1" onClick={onContact} />
    </div>
  );
};

const Tile = ({ icon, label, color, onClick }) => (
  <div
    onClick={onClick}
    style={{ textAlign: "center", cursor: "pointer", width: 72 }}
  >
    <div
      style={{
        width: 44,
        height: 44,
        borderRadius: "50%",
        background: color,
        color: "#fff",
        display: "grid",
        placeItems: "center",
        fontSize: 20,
        margin: "0 auto 4px",
      }}
    >
      {icon}
    </div>
    <div style={{ fontSize: 11, color: "#54656f" }}>{label}</div>
  </div>
);

/**
 * The saved-reply list. Opened from the ⚡ button (with its own search box) or
 * by typing "/" at the start of the message, where the text after the slash
 * is the filter and the arrow keys / Enter pick from the composer itself.
 */
const QuickMessageList = ({ items, active, onPick, search, onSearch }) => (
  <div style={{ width: 340 }}>
    {onSearch ? (
      <Input
        autoFocus
        allowClear
        size="small"
        placeholder="Search quick messages"
        value={search}
        onChange={(event) => onSearch(event.target.value)}
        style={{ marginBottom: 6 }}
      />
    ) : null}
    {items.length ? (
      <div style={{ maxHeight: 280, overflowY: "auto" }}>
        {items.map((item, index) => (
          <div
            key={`${item.title}-${index}`}
            role="option"
            aria-selected={index === active}
            tabIndex={-1}
            onMouseDown={(event) => {
              // Keep focus in the composer so the caret position survives.
              event.preventDefault();
              onPick(item);
            }}
            style={{
              padding: "6px 8px",
              borderRadius: 6,
              cursor: "pointer",
              background: index === active ? "#f0f5ff" : "transparent",
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 13 }}>{item.title}</div>
            <div
              style={{
                fontSize: 12,
                color: "#667781",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                whiteSpace: "pre-wrap",
              }}
            >
              {item.text}
            </div>
          </div>
        ))}
      </div>
    ) : (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No quick messages match" />
    )}
  </div>
);

const matches = (item, filter) => {
  const needle = filter.trim().toLowerCase();
  return !needle
    || item.title.toLowerCase().includes(needle)
    || item.text.toLowerCase().includes(needle);
};

const ChatComposer = ({
  value,
  onChange,
  onSend,
  sending,
  disabled,
  placeholder = "Type a message",
  header,
  attachmentContent,
  onAttach,
  attachAccept,
  quickMessages,
  accentColor,
  enterSends = true,
}) => {
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickSearch, setQuickSearch] = useState("");
  const [quickActive, setQuickActive] = useState(0);
  const textRef = useRef(null);

  const hasQuick = Array.isArray(quickMessages) && quickMessages.length > 0;
  // "/pric" at the start of a one-line message is a quick-message search.
  const slashFilter =
    hasQuick && value.startsWith("/") && !value.includes("\n") ? value.slice(1) : null;
  const quickItems = useMemo(
    () => (hasQuick
      ? quickMessages.filter((item) => matches(item, slashFilter ?? quickSearch))
      : []),
    [hasQuick, quickMessages, slashFilter, quickSearch]
  );

  const insertText = (insert) => {
    const el = textRef.current?.resizableTextArea?.textArea;
    const start = el ? el.selectionStart : value.length;
    const end = el ? el.selectionEnd : value.length;
    onChange(`${value.slice(0, start)}${insert}${value.slice(end)}`);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(start + insert.length, start + insert.length);
    });
  };

  const pickQuick = (item) => {
    setQuickOpen(false);
    setQuickSearch("");
    setQuickActive(0);
    if (slashFilter !== null) {
      onChange(item.text);
      requestAnimationFrame(() => textRef.current?.focus());
    } else {
      insertText(item.text);
    }
  };

  // Insert at the caret rather than appending, and keep the caret after it.
  const insertEmoji = (emoji) => insertText(emoji);

  const handleKeyDown = (event) => {
    if (slashFilter !== null && quickItems.length) {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        const step = event.key === "ArrowDown" ? 1 : -1;
        setQuickActive((index) => (index + step + quickItems.length) % quickItems.length);
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        pickQuick(quickItems[Math.min(quickActive, quickItems.length - 1)]);
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        onChange("");
        return;
      }
    }
    if (event.key !== "Enter") return;
    // Email bodies are multi-line by nature, so there Enter must not send.
    const shouldSend = enterSends ? !event.shiftKey : event.ctrlKey || event.metaKey;
    if (shouldSend) {
      event.preventDefault();
      onSend();
    }
  };

  return (
    <div style={{ background: "#f0f2f5", padding: "8px 12px" }}>
      {header}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
        <Popover
          open={emojiOpen}
          onOpenChange={setEmojiOpen}
          trigger="click"
          placement="topLeft"
          content={
            // The picker renders only while open (it is a heavy import and this
            // composer loads on two pages), but the wrapper must always exist:
            // antd never opens a Popover whose content is null.
            <div style={{ width: 320, height: 360 }}>
              {emojiOpen ? (
                <EmojiPicker onEmojiClick={(emoji) => insertEmoji(emoji.emoji)} height={360} width={320} />
              ) : null}
            </div>
          }
        >
          <Button type="text" icon={<SmileOutlined />} disabled={disabled} />
        </Popover>

        {attachmentContent ? (
          <Popover
            open={attachOpen}
            onOpenChange={setAttachOpen}
            trigger="click"
            placement="topLeft"
            content={
              <div onClick={() => setAttachOpen(false)}>{attachmentContent}</div>
            }
          >
            <Button type="text" icon={<PaperClipOutlined />} disabled={disabled} />
          </Popover>
        ) : onAttach ? (
          <Upload
            accept={attachAccept}
            showUploadList={false}
            beforeUpload={(file) => {
              onAttach(file);
              // Returning false keeps antd from trying to upload it itself.
              return false;
            }}
          >
            <Button type="text" icon={<PaperClipOutlined />} disabled={disabled} title="Attach a file" />
          </Upload>
        ) : null}

        {hasQuick ? (
          <Popover
            open={quickOpen && slashFilter === null}
            onOpenChange={(open) => {
              setQuickOpen(open);
              if (!open) setQuickSearch("");
            }}
            trigger="click"
            placement="topLeft"
            title="Quick messages"
            content={
              <QuickMessageList
                items={quickItems}
                active={-1}
                onPick={pickQuick}
                search={quickSearch}
                onSearch={setQuickSearch}
              />
            }
          >
            <Tooltip title="Quick messages ( / )">
              <Button type="text" icon={<ThunderboltOutlined />} disabled={disabled} />
            </Tooltip>
          </Popover>
        ) : null}

        <Popover
          open={slashFilter !== null}
          placement="topLeft"
          title="Quick messages · ↑↓ to choose, Enter to insert"
          content={
            <QuickMessageList
              items={quickItems}
              active={Math.min(quickActive, Math.max(quickItems.length - 1, 0))}
              onPick={pickQuick}
            />
          }
        >
        <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
        <TextArea
          ref={textRef}
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
            setQuickActive(0);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoSize={{ minRows: 1, maxRows: 5 }}
          style={{ borderRadius: 8, resize: "none", paddingRight: enterSends && !value ? (hasQuick ? 300 : 190) : undefined }}
        />
        {enterSends && !value ? (
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              right: 12,
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: 11.5,
              color: "#8696a0",
              pointerEvents: "none",
              whiteSpace: "nowrap",
            }}
          >
            {hasQuick ? "/ for quick messages · " : ""}Shift + Enter for a new line
          </span>
        ) : null}
        </div>
        </Popover>

        <Button
          type="primary"
          shape="circle"
          icon={<SendOutlined />}
          loading={sending}
          disabled={disabled}
          onClick={onSend}
          style={{ backgroundColor: accentColor, borderColor: accentColor }}
        />
      </div>
      {!enterSends && (
        <div style={{ fontSize: 10.5, color: "#8696a0", marginTop: 4 }}>
          Ctrl+Enter to send
        </div>
      )}
    </div>
  );
};

export default ChatComposer;
